/** The screen state from the entries on the phone, so that the route moves on with no signal. */

import type * as api from "../api";
import { PARTIAL_CODES } from "../today";
import { type NewEntry, type OutboxEntry, waiting } from "./outbox";

type StopState = api.RouteStop["state"];
type StopBody = { state: StopState; reason?: string; gallons_left?: number; raw_gal?: number; scrap_gal?: number; load_id?: string; at: string; change_id?: string; place?: api.Place };
type StartBody = api.RouteStart & { id: string; at: string };

const routePath = (id: string) => `/routes/${encodeURIComponent(id)}`;
const VISIT_STOP_STATE: Record<api.VisitKind, StopState> = { pickup: "done", level: "measured", no_access: "no_access", skipped: "skipped" };

export const stopEntry = (routeId: string, stopId: string, body: StopBody, label: string): NewEntry => ({
  id: stopId,
  kind: "stop",
  path: `${routePath(routeId)}/stops/${encodeURIComponent(stopId)}/visit`,
  body,
  route_id: routeId,
  label,
});

/** A change of a saved stop result (SO-1033). Each change has its own id, so the phone sends it one time. */
export const changeEntry = (routeId: string, stopId: string, changeId: string, body: StopBody, label: string): NewEntry => ({
  ...stopEntry(routeId, stopId, { ...body, change_id: changeId }, label),
  id: changeId,
});

/** The stop of a stop entry: a first result has the stop id, a change has its own id. */
export const entryStop = (entry: Pick<OutboxEntry, "id" | "kind" | "path">) =>
  entry.kind !== "stop" ? null : decodeURIComponent(/\/stops\/([^/]+)\/visit$/.exec(entry.path)?.[1] ?? entry.id);

export const startEntry = (routeId: string, body: StartBody, label: string): NewEntry => ({
  id: body.id,
  kind: "start",
  path: `${routePath(routeId)}/start`,
  body: { ...body },
  route_id: routeId,
  label,
});

export const finishEntry = (routeId: string, body: { odometer: number; at: string }, label: string): NewEntry => ({
  id: `finish-${routeId}`,
  kind: "finish",
  path: `${routePath(routeId)}/finish`,
  body,
  route_id: routeId,
  label,
});

export const loadEntry = (body: api.LoadEntry, label: string): NewEntry => ({ id: body.id, kind: "load", path: "/loads", body: { ...body }, label });

export const pickupEntry = (accountId: string, body: api.PickupEntry, label: string, routeId: string | null = null): NewEntry => ({
  id: body.id,
  kind: "pickup",
  path: `/accounts/${encodeURIComponent(accountId)}/pickups`,
  body: { ...body },
  route_id: routeId,
  label,
});

export const visitEntry = (body: api.VisitEntry, label: string): NewEntry => ({ id: body.id, kind: "visit", path: "/visits", body: { ...body }, label });

export const photoEntry = (accountId: string, body: api.PhotoEntry, label: string, routeId: string | null = null): NewEntry => ({
  id: body.id,
  kind: "photo",
  path: `/accounts/${encodeURIComponent(accountId)}/photos`,
  body: { ...body },
  route_id: routeId,
  label,
});

/** The photos of one stop, or of one account with no stop, that are on this phone. */
export const localPhotos = (all: readonly OutboxEntry[], accountId: string, stopId: string | null) =>
  all.filter((entry) => entry.kind === "photo" && entry.path === `/accounts/${encodeURIComponent(accountId)}/photos` && (entry.body.route_stop_id ?? null) === stopId);

/** The server count, plus each photo on the phone that the server day cannot hold: not sent, or sent after the day was asked for. */
export const stopPhotoCount = (server: number, dayAt: number, all: readonly OutboxEntry[], accountId: string, stopId: string | null) =>
  server + localPhotos(all, accountId, stopId).filter((entry) => entry.state !== "sent" || (entry.sent_at ?? 0) > dayAt).length;

function closeStop(route: api.Route, stopId: string, fields: Partial<api.RouteStop>, at: string): api.Route {
  const stops = route.stops.map((stop) => (stop.id === stopId && stop.state === "planned" ? { ...stop, ...fields } : stop));
  const started = route.status === "planned" ? "started" : route.status;
  const status = started === "started" && stops.every((stop) => stop.state !== "planned") ? "done" : started;
  return { ...route, stops, status, started_at: route.started_at ?? at };
}

/** The partial code of a stop body: the phone sends the code, an old entry can hold the words. */
const partialCode = (reason: string | undefined) => (reason === "frozen" || reason === "truck_full" ? reason : (PARTIAL_CODES[reason ?? ""] ?? null));

function changeStop(route: api.Route, stopId: string, fields: Partial<api.RouteStop>): api.Route {
  return { ...route, stops: route.stops.map((stop) => (stop.id === stopId && stop.state !== "planned" ? { ...stop, ...fields } : stop)) };
}

function startRoute(day: api.RouteDay, route: api.Route, body: StartBody): api.Route {
  const labels = new Map((day.check_items ?? []).map((item) => [item.id, item.label]));
  const results = body.results.map((result) => ({ ...result, label: labels.get(result.item_id) ?? result.item_id }));
  return {
    ...route,
    status: route.status === "planned" ? "started" : route.status,
    started_at: route.started_at ?? body.at,
    odometer_start: body.odometer,
    can_check: false,
    check: route.check ?? { at: body.at, odometer: body.odometer, failed: results.filter((result) => !result.ok).length, results },
  };
}

function apply(day: api.RouteDay, route: api.Route, entry: OutboxEntry): api.Route {
  if (entry.kind === "start" && entry.route_id === route.id) return startRoute(day, route, entry.body as unknown as StartBody);
  if (entry.kind === "finish" && entry.route_id === route.id) return { ...route, odometer_end: entry.body.odometer as number };
  if (entry.kind === "stop" && entry.route_id === route.id) {
    const body = entry.body as StopBody;
    const partial = body.state === "partial" ? { outcome_reason: partialCode(body.reason) } : {};
    const fields = { state: body.state, reason: body.reason ?? "", gallons_left: body.gallons_left ?? null, ...partial };
    if (!body.change_id) return closeStop(route, entry.id, fields, body.at);
    return changeStop(route, entryStop(entry) ?? entry.id, {
      ...fields,
      reason: body.state === "done" || body.state === "partial" ? "" : fields.reason,
      outcome_reason: body.state === "partial" ? partialCode(body.reason) : null,
      raw_gal: body.raw_gal ?? null,
      scrap_gal: body.raw_gal === undefined ? null : (body.scrap_gal ?? 0),
    });
  }
  if (entry.kind === "visit") {
    const body = entry.body as unknown as api.VisitEntry;
    const stopId = body.route_stop_id ?? freeStop(day, body);
    if (stopId && route.stops.some((stop) => stop.id === stopId)) {
      return closeStop(route, stopId, { state: VISIT_STOP_STATE[body.kind], gallons_left: body.gallons_left ?? null }, body.at);
    }
  }
  return route;
}

/** As the server does: a visit with no stop closes the one planned stop of its account on the visit day. */
function freeStop(day: api.RouteDay, body: api.VisitEntry): string | null {
  if (body.at.slice(0, 10) !== day.day) return null;
  const found = day.routes
    .filter((route) => route.mine && (route.status === "planned" || route.status === "started"))
    .flatMap((route) => route.stops.filter((stop) => stop.state === "planned" && stop.account_id === body.account_id));
  return found.length === 1 ? found[0].id : null;
}

/** The entries that the server day does not hold yet: each that waits, and each sent after the day was asked for. */
export const notInDay = (all: readonly OutboxEntry[], dayAt: number) =>
  all.filter((entry) => waiting(entry) || (entry.state === "sent" && (entry.sent_at ?? 0) > dayAt));

/** The day as the server will have it after each entry that waits on the phone, or was sent after `dayAt`. */
export function withLocal(day: api.RouteDay, all: readonly OutboxEntry[], dayAt: number = Infinity): api.RouteDay {
  const open = notInDay(all, dayAt);
  if (!open.length) return day;
  return { ...day, routes: day.routes.map((route) => open.reduce((next, entry) => apply(day, next, entry), route)) };
}

/** As GET /visits/defaults does: the next stop and the pre-trip state, from the kept day and the entries on the phone. */
export function localVisitDefaults(defaults: api.VisitDefaults, day: api.RouteDay | null, all: readonly OutboxEntry[], dayAt: number): api.VisitDefaults {
  if (!day) return defaults;
  const shown = withLocal(day, all, dayAt);
  const routes = shown.routes
    .filter((route) => route.mine && (route.status === "planned" || route.status === "started"))
    .sort((a, b) => a.truck.localeCompare(b.truck));
  for (const route of routes) {
    const stop = [...route.stops].sort((a, b) => a.position - b.position).find((found) => found.state === "planned");
    if (!stop) continue;
    const items = shown.check_items ? shown.check_items.length > 0 : Boolean(defaults.pre_trip_needed);
    const waits = route.status === "planned" && !route.started_at && items;
    return { ...defaults, next_stop: waits ? null : { account_id: stop.account_id, route_stop_id: stop.id }, pre_trip_needed: waits };
  }
  return { ...defaults, next_stop: null, pre_trip_needed: false };
}

/** The loads with each load that waits on the phone, and the gallons of the pickups that wait. */
export function withLocalLoads(loads: readonly api.Load[], all: readonly OutboxEntry[], made: string = new Date().toISOString()): api.Load[] {
  const open = all.filter(waiting);
  const known = new Set(loads.map((load) => load.id));
  const added = open.flatMap((entry): api.Load[] => {
    if (entry.kind !== "load" || known.has(entry.id)) return [];
    const body = entry.body as unknown as api.LoadEntry;
    const number = Math.max(0, ...loads.filter((load) => load.day === body.day && load.truck === body.truck).map((load) => load.number)) + 1;
    return [
      {
        id: body.id,
        code: "new",
        day: body.day,
        truck: body.truck,
        number,
        mine: true,
        made_at: made,
        stops: [],
        hidden_stops: 0,
        raw_gal: 0,
        scrap_gal: 0,
        result: null,
        approval: null,
        preview: null,
        can_edit: true,
        can_record_result: false,
        can_approve: false,
      },
    ];
  });
  const gallons = new Map<string, { raw: number; scrap: number }>();
  for (const entry of open) {
    const body = entry.body as { load_id?: string; raw_gal?: number; scrap_gal?: number };
    if (!body.load_id || typeof body.raw_gal !== "number") continue;
    const sum = gallons.get(body.load_id) ?? { raw: 0, scrap: 0 };
    gallons.set(body.load_id, { raw: sum.raw + body.raw_gal, scrap: sum.scrap + (body.scrap_gal ?? 0) });
  }
  return [...loads, ...added].map((load) => {
    const sum = gallons.get(load.id);
    return sum ? { ...load, raw_gal: load.raw_gal + sum.raw, scrap_gal: load.scrap_gal + sum.scrap } : load;
  });
}

/** The state of the entry for each stop, route and visit id: Saved on phone, Sent or Not saved. */
export function entryStates(all: readonly OutboxEntry[]): Map<string, OutboxEntry> {
  const found = new Map<string, OutboxEntry>();
  for (const entry of all) {
    if (entry.kind === "stop") found.set(entryStop(entry) ?? entry.id, entry);
    if (entry.kind === "visit") found.set(entry.id, entry);
    if (entry.kind === "visit" && typeof entry.body.route_stop_id === "string") found.set(entry.body.route_stop_id, entry);
    if ((entry.kind === "start" || entry.kind === "finish") && entry.route_id) found.set(`${entry.kind}:${entry.route_id}`, entry);
  }
  return found;
}
